import { type ReactNode, useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useWindowSize } from "usehooks-ts";

import { useSelector } from "@/redux/hooks";
import { getUserAgent } from "@/redux/selectors/userAgent";

const MobileContentLayout = dynamic(() => import("./MobileContentLayout"), {
  ssr: true,
});

const MOBILE_WIDTH_THRESHOLD = 768;

export interface ContentLayoutProps {
  toggleFilterList: (open?: boolean) => void;
  showMapButton: boolean;
}

const toggleFilterListNoOp = (_open?: boolean) => {};

const DesktopContentLayout = ({
  children,
}: {
  children: (props: ContentLayoutProps) => ReactNode;
}) => {
  return (
    <div
      className="h-full min-h-0 relative"
      id="content-layout"
      data-testid="content-layout-desktop"
    >
      {children({
        showMapButton: false,
        toggleFilterList: toggleFilterListNoOp,
      })}
    </div>
  );
};

export default ({
  children,
  showBanner,
  mobileThreshold = MOBILE_WIDTH_THRESHOLD,
}: {
  children: (props: ContentLayoutProps) => ReactNode;
  showBanner: boolean;
  mobileThreshold?: number;
}) => {
  // detect mobile from user agent
  const { isMobile: isMobilePlatform } = useSelector(getUserAgent);
  // or allow desktop mobile styling
  const { width } = useWindowSize();
  const isMobileWidth = width < mobileThreshold;

  const [isMobile, setIsMobile] = useState(isMobilePlatform);
  // this allows the client to override the mobile detection
  // without this useEffect, there will be a react hydration mismatch
  useEffect(() => {
    setIsMobile(isMobilePlatform || isMobileWidth);
  }, [isMobilePlatform, isMobileWidth]);

  const Wrapper = useMemo(
    () => (isMobile ? MobileContentLayout : DesktopContentLayout),
    [isMobile],
  );

  return (
    <Wrapper showBanner={showBanner} mobileThreshold={mobileThreshold}>
      {children}
    </Wrapper>
  );
};
